<script lang="ts">
  import { PUBLIC_API_URL } from '$env/static/public';

  import { afterNavigate, goto } from '$app/navigation';
  import { page } from '$app/stores';

  import { onMount } from 'svelte';

  import { get } from 'svelte/store';

  import { Order } from '@shared/types';
  import { ClientOnly } from '@shared/ui/client-only';
  import { Container } from '@shared/ui/container';
  import { Content } from '@shared/ui/content';
  import { GridToggler } from '@shared/ui/grid-toggler';
  import { Heading } from '@shared/ui/heading';
  import { cn, is, serializeSearchParams } from '@shared/utils';

  import type { ArtAssetReadManyDto } from '@entities/art-asset';
  import type { ArtistType } from '@entities/artist';
  import { SelectionApi, type SelectionsReadManyResponse } from '@entities/selection';

  import { ArtworksFilter } from '@features/artwork/artworks-filter';
  import { ArtworksSort } from '@features/artwork/artworks-sort';
  import { Pagination } from '@features/pagination';

  import { ArtworkList } from '@widgets/artwork-list';
  import { ArtistArtworksFilterSidebar, filtersLibs } from '@widgets/artworks-filter-sidebar';
  import { SelectionList } from '@widgets/selection-list';

  import { ARTWORKS_PER_PAGE } from '@pages/artworks';
  import { SELECTIONS_PER_PAGE } from '@pages/selections/configs';

  import { artworksSortOptions, defaultSortParams } from '../configs';
  import ArtistInfo from './artist-info.svelte';
  import ArtistTabs from './artist-tabs.svelte';
  import { configureSearchParams } from '../libs';

  export let artist: ArtistType;
  export let artworks: ArtAssetReadManyDto['art_assets'];
  export let artworksCount: number;
  export let pageNumber: number;
  export let loadMore: number;
  export let hasParams: boolean;
  export let selectionsCount: number;
  export let selections: SelectionsReadManyResponse['selections'];

  const defaultParams = {
    column: 'year',
    order: 'desc',
  } as const;

  const selectionApi = new SelectionApi(fetch);

  let container: HTMLElement;
  let artworksContainer: HTMLElement;
  let currentPage = pageNumber;
  let prevPage = pageNumber;
  let loading = false;
  let isMounted = false;

  let sortField = $page.url.searchParams.get('column') || defaultSortParams.column;
  let sortOrder: Order = ($page.url.searchParams.get('order') as Order) || defaultSortParams.order;

  let selectionsLoading = false;

  async function requestRedirect(params: { page: number; loadMore?: number; invalidateAll?: boolean }) {
    const { url, params: pageParams } = get(page);

    const newPage = params?.page || 1;
    const isSamePage = newPage === prevPage;

    url.searchParams.set('order', `${sortOrder}`);
    url.searchParams.set('column', `${sortField}`);

    if (!params.loadMore) {
      url.searchParams.delete('loadMore');
    }

    const searchParamsString = serializeSearchParams(url.searchParams);

    if (isSamePage) {
      await goto(searchParamsString, {
        noScroll: isSamePage,
        replaceState: true,
        invalidateAll: true,
      });
    } else {
      const filters = filtersLibs.getFiltersFromSlug(pageParams.slug);

      const artistUrl = `/artist-${artist.id}-${artist.slug}`;

      const newUrl = `${is(newPage !== 1, `${artistUrl}/${filters}page-${newPage}`, `${artistUrl}/${filters}`)}${searchParamsString}`;

      prevPage = newPage;
      await goto(newUrl, { invalidateAll: params.invalidateAll ?? false, noScroll: true });
    }
  }

  async function handleLoadMore(params: { page: number; loadMore?: number }) {
    const { url, params: pageParams } = get(page);

    url.searchParams.set('loadMore', `${params.loadMore}`);

    const searchParams = configureSearchParams({
      artistId: `${artist.id}`,
      configurationId: $page.data.configurations.id,
      loadMore: params.loadMore,
      page: params.page,
      pageSize: ARTWORKS_PER_PAGE,
      slug: pageParams.slug,
      sortField,
      sortOrder,
    });

    const someReceivedArtworks = await handleLoadArtworks(searchParams);

    artworks = [...artworks, ...someReceivedArtworks];

    await goto(`?${url.searchParams.toString()}`, {
      noScroll: true,
      replaceState: true,
      invalidateAll: false,
    });
  }

  async function handleLoadArtworks(params: URLSearchParams) {
    const searchParams = params.toString();

    try {
      const resp: any = await fetch(`${PUBLIC_API_URL}/art-assets?${searchParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const { art_assets } = await resp.json();

      return art_assets;
    } catch (error) {
      console.error('Error fetching artworks', error);

      return [];
    }
  }

  const applySort = async (field: string, order: string) => {
    if (!isMounted || !field || !order) return;

    loading = true;
    loadMore = 0;

    await requestRedirect({ page: 1, loadMore, invalidateAll: true });

    loading = false;
  };

  async function selectionsRequestRedirect(params: { page: number; loadMore?: number; invalidateAll?: boolean }) {
    const { url, params: pageParams } = get(page);
    const newPage = params?.page || 1;
    const isSamePage = newPage === prevPage;

    if (!params.loadMore) {
      url.searchParams.delete('loadMore');
    }

    const searchParamsString = serializeSearchParams(url.searchParams);

    if (isSamePage) {
      await goto(searchParamsString, {
        noScroll: true,
        replaceState: true,
        invalidateAll: true,
      });
    } else {
      const filters = filtersLibs.getFiltersFromSlug(pageParams.slug);

      const artistUrl = `/artist-${artist.id}-${artist.slug}`;

      const newUrl = `${is(newPage !== 1, `${artistUrl}/${filters}page-${newPage}`, `${artistUrl}/${filters}`)}${searchParamsString}`;

      prevPage = newPage;

      await goto(newUrl, { invalidateAll: params.invalidateAll ?? false, noScroll: true });
    }
  }

  async function handleSelectionsLoadMore(params: { page: number; loadMore?: number }) {
    const { url, params: pageParams } = get(page);
    url.searchParams.set('loadMore', `${params.loadMore}`);
    const searchParams = configureSearchParams({
      artistId: `${artist.id}`,
      configurationId: $page.data.configurations.id,
      loadMore: params.loadMore,
      page: params.page,
      pageSize: SELECTIONS_PER_PAGE,
      slug: pageParams.slug,
    });

    const loadedSelections = (await selectionApi.readMany(searchParams)).selections;

    selections = [...selections, ...loadedSelections];

    const searchParamsString = serializeSearchParams($page.url.searchParams);

    await goto(searchParamsString, {
      noScroll: true,
      replaceState: true,
      invalidateAll: false,
    });
  }

  function handleSwitch(e: CustomEvent<number>) {
    currentPage = e.detail;
    prevPage = e.detail;
  }

  $: applySort(sortField, sortOrder);

  onMount(() => {
    isMounted = true;
  });

  afterNavigate(({ to }) => {
    if (to?.url.searchParams?.size === 0) {
      sortField = defaultParams.column;
      sortOrder = defaultParams.order;
    }
  });
</script>

<ArtistInfo {artist} />

<ArtistTabs
  hasBiography="{!!artist?.biography?.blocks?.length}"
  {artworksCount}
  {selectionsCount}
  on:switch="{handleSwitch}"
>
  <div slot="biography">
    <Container containerClass="py-8 md:py-12 max-w-[648px] md:px-0 lg:px-0 xl:px-0">
      <h2 class="mb-3 text-lg font-medium">{`${artist.name} biography`}</h2>

      <Content blocks="{artist?.biography?.blocks || []}" />
    </Container>
  </div>

  <div slot="artworks" class="pt-8 md:pt-12">
    <Heading
      containerClass="mb-0 md:mb-0 lg:mb-0 mt-0 md:mt-0 lg:mt-0"
      title="{`${artist.name} artworks in digital format`}"
      titleClass="inline text-balance text-lg md:text-lg lg:text-lg"
      wrapperClass="{cn('flex flex-col items-start gap-6 md:gap-8 lg:items-end lg:gap-12 lg:flex-row normal-case', {
        'justify-center md:items-center': artworksCount === 1,
      })}"
      dividePosition="none"
      tag="h2"
      counter="{artworksCount > 1 ? artworksCount : null}"
    >
      {#if artworksCount > 1}
        <div class="flex items-center justify-center gap-9">
          {#if hasParams || artworksCount > 10}
            <!-- show filter only for more than 10 artworks and in case if filters applied (that means that total is already was > 10) -->
            <ArtworksFilter />
          {/if}
          <div class="w-[168px]">
            <ArtworksSort bind:field="{sortField}" bind:order="{sortOrder}" sortOptions="{artworksSortOptions}" />
          </div>

          <GridToggler />
        </div>
      {/if}
    </Heading>

    <section bind:this="{artworksContainer}">
      <Container containerClass="py-8 md:py-12">
        <Pagination
          isFullWidth
          containerToScroll="{artworksContainer}"
          pageSize="{ARTWORKS_PER_PAGE}"
          requestFunction="{requestRedirect}"
          loadMoreFunction="{handleLoadMore}"
          bind:currentPage
          bind:loadMore
          bind:totalDataCount="{artworksCount}"
          bind:loading
        >
          <ArtworkList {artworks} wrapperClasses="{cn({ 'justify-center': artworks.length === 1 })}" />
        </Pagination>
      </Container>
    </section>
  </div>

  <div slot="selections" class="px-5 py-10 md:px-8 md:py-12 lg:px-12">
    <section bind:this="{container}" class="wrapper-container">
      <Heading
        containerClass="mt-0 md:mt-0 lg:mt-0 px-0 md:px-0"
        title="{`${artist.name} artworks in selections`}"
        titleClass="inline text-balance text-lg md:text-lg lg:text-lg"
        wrapperClass="{cn('flex flex-col items-start gap-6 md:gap-8 lg:items-end lg:gap-12 lg:flex-row normal-case', {
          'justify-center md:items-center': selectionsCount === 1,
        })}"
        dividePosition="none"
        tag="h2"
        counter="{selectionsCount > 1 ? selectionsCount : null}"
      ></Heading>
      <Pagination
        isFullWidth
        containerToScroll="{container}"
        pageSize="{SELECTIONS_PER_PAGE}"
        requestFunction="{selectionsRequestRedirect}"
        loadMoreFunction="{handleSelectionsLoadMore}"
        bind:currentPage
        bind:loadMore
        bind:totalDataCount="{selectionsCount}"
        bind:loading="{selectionsLoading}"
      >
        <Container containerClass="mb-5 px-0 md:px-0 lg:px-0">
          <SelectionList list="{selections}" classes="{cn({ 'flex justify-center': selections.length === 1 })}" />
        </Container>
      </Pagination>
    </section>
  </div>
</ArtistTabs>

<ClientOnly>
  <ArtistArtworksFilterSidebar {artist} />
</ClientOnly>
